import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const GA_ID = 'G-K656E4E0L8';

const LeadCapture = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [optin, setOptin] = useState(true); // implicit intent of this form IS the newsletter
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const labels = {
    title: 'Otrzymuj jako pierwszy nowe nieruchomości na Costa Blanca',
    subtitle: 'Zostaw swój adres e-mail i otrzymuj aktualności o nowej ofercie, obniżkach cen i ciekawych projektach.',
    firstName: 'Imię',
    email: 'Adres e-mail',
    submit: 'Otrzymuj aktualności',
    submitting: 'Wysyłanie...',
    confirm: 'Dziękujemy! Za chwilę zostaniesz przekierowany do aktualnej oferty nieruchomości.',
    privacy: 'Szanujemy Twoją prywatność. Twoje dane są bezpiecznie przechowywane i nigdy nie udostępniane.',
    optin: 'Tak, wysyłajcie mi newsletter CasaOthers z nową ofertą i poradami (maks. 2× w miesiącu). W każdej chwili możesz się wypisać.',
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !email.trim()) {
      setError('Wprowadź swoje imię i adres e-mail.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          source: 'homepage',
          newsletter_optin: optin,
        }),
      });
      if (!res.ok) throw new Error('submit failed');

      // GA4 conversion event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          send_to: GA_ID,
          form_location: 'homepage',
          value: 1,
          currency: 'EUR',
        });
      }

      setDone(true);
      // Centralised conversion page so Google Ads can track this as a goal.
      setTimeout(() => navigate('/bedankt'), 600);
    } catch (err) {
      setError('Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={styles.section} data-testid="lead-capture-section">
      <div style={styles.container}>
        <div style={styles.copyCol}>
          <h2 style={styles.title} data-testid="lead-capture-title">{labels.title}</h2>
          <p style={styles.subtitle}>{labels.subtitle}</p>
        </div>

        {done ? (
          <div style={styles.confirmation} data-testid="lead-capture-confirmation">
            <p style={styles.confirmText}>{labels.confirm}</p>
          </div>
        ) : (
          <form style={styles.form} onSubmit={onSubmit} data-testid="lead-capture-form" noValidate>
            <input
              type="text"
              placeholder={labels.firstName}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={styles.input}
              required
              autoComplete="given-name"
              data-testid="lead-firstname-input"
            />
            <input
              type="email"
              placeholder={labels.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
              autoComplete="email"
              data-testid="lead-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              style={{ ...styles.btn, ...(submitting ? styles.btnDisabled : {}) }}
              data-testid="lead-submit-button"
            >
              {submitting ? labels.submitting : labels.submit}
            </button>
            <label style={styles.optinLabel} data-testid="lead-optin-wrap">
              <input
                type="checkbox"
                checked={optin}
                onChange={(e) => setOptin(e.target.checked)}
                style={styles.optinCheckbox}
                data-testid="lead-optin-checkbox"
              />
              <span>{labels.optin}</span>
            </label>
            {error && (
              <p style={styles.error} data-testid="lead-error">{error}</p>
            )}
            <p style={styles.privacy}>{labels.privacy}</p>
          </form>
        )}
      </div>
    </section>
  );
};

const styles = {
  section: {
    backgroundColor: '#1A1918',
    color: 'white',
    padding: '6rem 1.5rem',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '3rem',
    alignItems: 'center',
  },
  copyCol: { maxWidth: '520px' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
    margin: '0 0 1rem',
    color: 'white',
    lineHeight: 1.2,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: '1rem',
    lineHeight: 1.65,
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    maxWidth: '420px',
    justifySelf: 'end',
  },
  input: {
    padding: '1rem 1.25rem',
    border: '1px solid rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: 'white',
    fontSize: '1rem',
    fontFamily: "'Manrope', sans-serif",
    outline: 'none',
  },
  btn: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.3s ease',
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  error: {
    color: '#E89A9A',
    fontSize: '0.875rem',
    margin: 0,
  },
  privacy: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.75rem',
    margin: '0.25rem 0 0',
    lineHeight: 1.5,
  },
  optinLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem',
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.8rem',
    lineHeight: 1.5,
    cursor: 'pointer',
    marginTop: '0.25rem',
  },
  optinCheckbox: {
    marginTop: '0.15rem',
    width: '16px',
    height: '16px',
    accentColor: '#C5A059',
    flexShrink: 0,
    cursor: 'pointer',
  },
  confirmation: {
    backgroundColor: 'rgba(197, 160, 89, 0.12)',
    border: '1px solid #C5A059',
    padding: '2rem',
    textAlign: 'center',
    width: '100%',
    maxWidth: '420px',
    justifySelf: 'end',
  },
  confirmText: {
    color: 'white',
    fontSize: '1.05rem',
    margin: 0,
    lineHeight: 1.5,
  },
};

export default LeadCapture;
